import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { duelService } from '../../../services/socialService';
import { useDuelRealtime } from '../../../hooks/useDuelRealtime';
import ProofPhotoUpload from './ProofPhotoUpload';
import ProofPhotoGallery from './ProofPhotoGallery';
import CompetitiveProgressBar from './CompetitiveProgressBar';
import DuelActivityFeed from './DuelActivityFeed';
import RealtimeVerificationModal from './RealtimeVerificationModal';

const DuelDetailModal = ({ isOpen, onClose, duel: initialDuel, userId, onDuelUpdated }) => {
  const [duel, setDuel] = useState(initialDuel);
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [loadingProofs, setLoadingProofs] = useState(false);
  const [showRealtimeVerification, setShowRealtimeVerification] = useState(false);
  const [realtimeUpdateKey, setRealtimeUpdateKey] = useState(0);

  // Update local state when prop changes
  useEffect(() => {
    setDuel(initialDuel);
  }, [initialDuel]);

  // Subscribe to real-time updates for this duel
  useDuelRealtime(duel?.id, userId, {
    onDuelUpdate: (payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new) {
        setDuel(prev => ({ ...prev, ...payload?.new }));
        onDuelUpdated?.(); // Notify parent to refresh
      }
    },
    onProofUpload: async (payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        // Fetch full proof data with user info
        try {
          const proofsData = await duelService?.getDuelProofs(duel?.id);
          setProofs(proofsData || []);
          setRealtimeUpdateKey(prev => prev + 1); // Trigger re-render
          onDuelUpdated?.(); // Notify parent to refresh progress
        } catch (error) {
          console.error('Error refetching proofs:', error);
        }
      }
    },
    onVerificationComplete: (payload) => {
      // Show notification when verification completes
      if (payload?.new?.auto_verified) {
        setRealtimeUpdateKey(prev => prev + 1);
      }
    }
  });

  const isCreator = duel?.creator_id === userId;
  const opponent = isCreator ? duel?.opponent : duel?.creator;
  const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
  const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;
  const isPending = duel?.status === 'pending';
  const isActive = duel?.status === 'active';
  const isCompleted = duel?.status === 'completed';
  const isOpponent = duel?.opponent_id === userId;
  const isWinner = duel?.winner_id === userId;

  const progressPercentage = Math?.min((userProgress / duel?.target_value) * 100, 100);
  const opponentProgressPercentage = Math?.min((opponentProgress / duel?.target_value) * 100, 100);

  const handleAcceptDuel = async () => {
    setLoading(true);
    setActionType('accept');
    try {
      await duelService?.acceptDuel(duel?.id, userId);
      onDuelUpdated();
      onClose();
    } catch (error) {
      console.error('Error accepting duel:', error);
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  const handleRejectDuel = async () => {
    setLoading(true);
    setActionType('reject');
    try {
      await duelService?.cancelDuel(duel?.id, userId);
      onDuelUpdated();
      onClose();
    } catch (error) {
      console.error('Error rejecting duel:', error);
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  useEffect(() => {
    if (isOpen && duel?.id && isActive) {
      loadDuelProofs();
    }
  }, [isOpen, duel?.id, isActive]);

  const loadDuelProofs = async () => {
    setLoadingProofs(true);
    try {
      const proofsData = await duelService?.getDuelProofs(duel?.id);
      setProofs(proofsData || []);
    } catch (error) {
      console.error('Error loading duel proofs:', error);
    } finally {
      setLoadingProofs(false);
    }
  };

  const handlePhotoUploaded = (newProof) => {
    setProofs(prev => [...prev, newProof]);
    // Optionally refresh duel data to update progress
    onDuelUpdated?.();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{duel?.title}</h2>
            <p className="text-sm text-muted-foreground">{duel?.description}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Participants */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="flex items-center space-x-3 mb-3">
                {duel?.creator?.avatar_url ? (
                  <img src={duel?.creator?.avatar_url} alt={duel?.creator?.full_name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-lg font-semibold text-primary">
                      {duel?.creator?.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-foreground">{duel?.creator?.full_name}</p>
                  <p className="text-xs text-muted-foreground">Retador</p>
                </div>
              </div>
              {isActive && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className="font-medium text-primary">{duel?.creator_progress} / {duel?.target_value}</span>
                  </div>
                  <div className="w-full bg-muted/50 rounded-full h-2">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${Math?.min((duel?.creator_progress / duel?.target_value) * 100, 100)}%` }} />
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-muted/30 rounded-xl border border-border">
              <div className="flex items-center space-x-3 mb-3">
                {opponent?.avatar_url ? (
                  <img src={opponent?.avatar_url} alt={opponent?.full_name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-lg font-semibold text-muted-foreground">
                      {opponent?.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-foreground">{opponent?.full_name}</p>
                  <p className="text-xs text-muted-foreground">Oponente</p>
                </div>
              </div>
              {isActive && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className="font-medium text-foreground">{opponentProgress} / {duel?.target_value}</span>
                  </div>
                  <div className="w-full bg-muted/50 rounded-full h-2">
                    <div className="h-2 rounded-full bg-muted-foreground" style={{ width: `${opponentProgressPercentage}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Competitive Progress Bar (for active duels) */}
          {isActive && duel && (
            <div className="mb-6">
              <CompetitiveProgressBar duel={duel} userId={userId} />
            </div>
          )}

          {/* Duel Details */}
          <div className="space-y-4 mb-6">
            <div className="p-4 bg-muted/30 rounded-xl">
              <h3 className="text-sm font-semibold text-foreground mb-3">Detalles del Duelo</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Meta</span>
                  <span className="text-sm font-medium text-foreground">{duel?.target_value}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Duración</span>
                  <span className="text-sm font-medium text-foreground">{duel?.duration_days} días</span>
                </div>
                {duel?.start_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Inicio</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Date(duel?.start_date)?.toLocaleDateString('es-ES')}
                    </span>
                  </div>
                )}
                {duel?.end_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Fin</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Date(duel?.end_date)?.toLocaleDateString('es-ES')}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Verificación</span>
                  <span className="text-sm font-medium text-foreground capitalize">
                    {duel?.verification_mode === 'realtime' ? 'Tiempo Real' : duel?.verification_mode}
                  </span>
                </div>
              </div>
            </div>

            {/* Custom Challenge Details */}
            {duel?.is_custom && (
              <div className="p-4 bg-primary/10 rounded-xl border border-primary/30">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Edit3" size={20} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Reto Personalizado</h3>
                </div>
                {duel?.custom_rules && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Reglas:</p>
                    <p className="text-sm text-foreground">{duel?.custom_rules}</p>
                  </div>
                )}
                {duel?.custom_reward && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Recompensa:</p>
                    <p className="text-sm text-foreground">{duel?.custom_reward}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Result */}
          {isCompleted && (
            <div className={`p-4 rounded-xl mb-6 ${
              isWinner ? 'bg-success/10 border border-success/30' : 'bg-error/10 border border-error/30'
            }`}>
              <div className="flex items-center space-x-3">
                <Icon name={isWinner ? 'Trophy' : 'X'} size={32} className={isWinner ? 'text-success' : 'text-error'} />
                <div>
                  <h3 className={`text-lg font-bold ${
                    isWinner ? 'text-success' : 'text-error'
                  }`}>
                    {isWinner ? '¡Ganaste el Duelo!' : 'Perdiste - Hora de Pagar'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {isWinner 
                      ? `${opponent?.full_name} debe cumplir el stake` 
                      : `Debes cumplir: ${duel?.stake?.title}`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {isPending && isOpponent && (
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleRejectDuel}
                loading={loading && actionType === 'reject'}
                disabled={loading}
                fullWidth
              >
                Rechazar
              </Button>
              <Button
                onClick={handleAcceptDuel}
                loading={loading && actionType === 'accept'}
                disabled={loading}
                fullWidth
              >
                Aceptar Duelo
              </Button>
            </div>
          )}

          {isPending && isCreator && (
            <div className="text-center p-4 bg-accent/10 rounded-xl">
              <Icon name="Clock" size={32} className="text-accent mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Esperando que {opponent?.full_name} acepte el duelo</p>
            </div>
          )}

          {isActive && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-warning/10 rounded-xl">
                <Icon name="Flame" size={32} className="text-warning mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground mb-1">Duelo en Curso</p>
                <p className="text-xs text-muted-foreground">
                  El progreso se actualiza automáticamente desde tus logs de actividad
                </p>
              </div>

              {/* Verification Section (for active duels) */}
              {isActive && (
                <div className="space-y-4 mb-6">
                  {duel?.verification_mode === 'realtime' ? (
                    <div className="p-4 bg-error/10 rounded-xl border border-error/30">
                      <div className="flex items-start space-x-3 mb-3">
                        <Icon name="Clock" size={24} className="text-error" />
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-1">Verificación en Tiempo Real</h4>
                          <p className="text-xs text-muted-foreground">
                            Recibirás una notificación sorpresa. Tendrás {duel?.verification_timer_minutes} minutos para capturar evidencia.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setShowRealtimeVerification(true)}
                        fullWidth
                        className="bg-error hover:bg-error/90"
                      >
                        <Icon name="Camera" size={16} className="mr-2" />
                        Iniciar Verificación Ahora
                      </Button>
                    </div>
                  ) : (
                    <ProofPhotoUpload 
                      duelId={duel?.id}
                      userId={userId}
                      onPhotoUploaded={handlePhotoUploaded}
                      duelTitle={duel?.title}
                    />
                  )}
                </div>
              )}

              {/* Activity Feed */}
              {isActive && (
                <div className="mb-6">
                  <DuelActivityFeed 
                    duelId={duel?.id}
                    userId={userId}
                    proofs={proofs}
                  />
                </div>
              )}

              {/* Photo Gallery */}
              {loadingProofs ? (
                <div className="text-center p-4">
                  <Icon name="Loader2" size={24} className="text-muted-foreground mx-auto animate-spin" />
                </div>
              ) : (
                <ProofPhotoGallery proofs={proofs} currentUserId={userId} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Realtime Verification Modal */}
      {showRealtimeVerification && (
        <RealtimeVerificationModal
          isOpen={showRealtimeVerification}
          onClose={() => setShowRealtimeVerification(false)}
          duelId={duel?.id}
          userId={userId}
          duelTitle={duel?.title}
          timerMinutes={duel?.verification_timer_minutes || 2}
          requireDualCamera={duel?.require_dual_camera || false}
          onPhotoUploaded={handlePhotoUploaded}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DuelDetailModal;
